/**
 * Client state for ระบบอนุมัติงาน (docs/api/APPROVALS.md, ADR 0027): Thai labels and chip tones for
 * the item states and request statuses, the status-chip batch lookup every list uses (one request
 * per list, shared and cached), and the summary behind the nav badge and the home card.
 *
 * `invalidateApprovals()` is called after every request / decision made in this tab, and the live
 * notification stream calls it when an approval notification arrives, so chips, the inbox and the
 * badge follow each other without a reload.
 */
import { useEffect, useMemo, useState } from 'react';
import { api } from '../api/client.js';
import type { ApprovalItemState, ApprovalItemStateView, ApprovalStatus, ApprovalSummary, ApprovalTargetType } from '../api/types.js';
import type { Tone } from '../components/Chip.js';
import { useSession } from './session.js';

export const ITEM_STATE: Record<ApprovalItemState, { label: string; tone: Tone; icon: string; hint: string }> = {
  draft: { label: 'ร่าง', tone: 'neutral', icon: '✎', hint: 'ยังไม่ได้ส่งขออนุมัติ' },
  pending: { label: 'รออนุมัติ', tone: 'warn', icon: '⏳', hint: 'ส่งขออนุมัติแล้ว รอผู้อนุมัติ' },
  changes_requested: { label: 'ต้องแก้ไข', tone: 'danger', icon: '!', hint: 'ผู้อนุมัติขอให้แก้ไข' },
  rejected: { label: 'ไม่อนุมัติ', tone: 'danger', icon: '✕', hint: 'ผู้อนุมัติไม่อนุมัติ' },
  approved: { label: 'อนุมัติแล้ว', tone: 'ok', icon: '✓', hint: 'ฉบับปัจจุบันได้รับอนุมัติแล้ว' },
  edited_after_approval: { label: 'แก้ไขหลังอนุมัติ', tone: 'info', icon: '↻', hint: 'มีการแก้ไขหลังฉบับที่อนุมัติ ต้องส่งขออนุมัติใหม่' },
};

export const REQUEST_STATUS: Record<ApprovalStatus, { label: string; tone: Tone; icon: string }> = {
  PENDING: { label: 'รออนุมัติ', tone: 'warn', icon: '⏳' },
  APPROVED: { label: 'อนุมัติแล้ว', tone: 'ok', icon: '✓' },
  CHANGES_REQUESTED: { label: 'ขอให้แก้ไข', tone: 'danger', icon: '!' },
  REJECTED: { label: 'ไม่อนุมัติ', tone: 'danger', icon: '✕' },
  WITHDRAWN: { label: 'ถอนคำขอ', tone: 'neutral', icon: '↩' },
  STALE: { label: 'แก้ไขหลังส่ง — ต้องส่งใหม่', tone: 'info', icon: '↻' },
};

export const TARGET_LABEL: Record<ApprovalTargetType, string> = { design: 'งานออกแบบ', tor: 'TOR', product: 'ข้อมูลสินค้า', file: 'ไฟล์' };

export const EVENT_LABEL: Record<string, string> = {
  requested: 'ส่งขออนุมัติ', approved: 'อนุมัติ', changes_requested: 'ขอให้แก้ไข', rejected: 'ไม่อนุมัติ', withdrawn: 'ถอนคำขอ', stale: 'งานถูกแก้ไขหลังส่ง — คำขอนี้ใช้ไม่ได้แล้ว',
  superseded: 'ส่งคำขอใหม่แทน', comment: 'แสดงความคิดเห็น', step_advanced: 'เข้าสู่ขั้นถัดไป', approver_added: 'เพิ่มผู้อนุมัติ', due_soon: 'ระบบเตือน: ใกล้ครบกำหนด', overdue: 'ระบบเตือน: เกินกำหนด', due_changed: 'เปลี่ยนวันกำหนด',
};

export const historyHref = (type: ApprovalTargetType, id: string) => `/approvals/history/${type}/${id}`;
export const reviewHref = (requestId: string) => `/approvals/${requestId}`;

const thaiDate = new Intl.DateTimeFormat('th-TH', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Bangkok' });
const thaiDateTime = new Intl.DateTimeFormat('th-TH', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Bangkok' });
export const formatDay = (v: string | null | undefined) => (v && !Number.isNaN(Date.parse(v)) ? thaiDate.format(new Date(v)) : '—');
export const formatWhen = (v: string | null | undefined) => (v && !Number.isNaN(Date.parse(v)) ? thaiDateTime.format(new Date(v)) : '—');

/** `datetime-local` value ⇄ ISO (the input is the browser's local wall clock). */
export const localToIso = (v: string) => (v ? new Date(v).toISOString() : null);

// ---------- change bus ----------

const listeners = new Set<() => void>();
let version = 0;

/** Something about approvals changed (a request, a decision, a notification): reload chips, inbox, badge. */
export function invalidateApprovals(): void {
  version++;
  states.clear();
  summaries.clear();
  for (const fn of listeners) fn();
}

function useApprovalsVersion(): number {
  const [v, setV] = useState(version);
  useEffect(() => {
    const fn = () => setV(version);
    listeners.add(fn);
    return () => {
      listeners.delete(fn);
    };
  }, []);
  return v;
}

/** Bumps when approvals changed; put it in an effect's dependencies to reload. */
export const useApprovalsTick = useApprovalsVersion;

// ---------- item states (chips) ----------

/** id → state, or null when the server has no answer for it (hidden / deleted / not in this workspace). */
const states = new Map<string, ApprovalItemStateView | null>();
const key = (w: string, type: ApprovalTargetType, id: string) => `${w}:${type}:${id}`;
const wanted = new Map<string, { w: string; type: ApprovalTargetType; ids: Set<string> }>();
const loadedListeners = new Set<() => void>();
let flushTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Every row of a list asks for its own chip; the asks of one render are collected and sent as ONE
 * request per work type (≤ 150 ids each), so a gallery of 60 cards costs one round trip.
 */
function want(w: string, type: ApprovalTargetType, id: string): void {
  const k = `${w}:${type}`;
  let entry = wanted.get(k);
  if (!entry) wanted.set(k, (entry = { w, type, ids: new Set() }));
  entry.ids.add(id);
  flushTimer ??= setTimeout(() => void flushWanted(), 30);
}

async function flushWanted(): Promise<void> {
  flushTimer = null;
  const batch = [...wanted.values()];
  wanted.clear();
  for (const { w, type, ids } of batch) {
    const list = [...ids].filter((id) => !states.has(key(w, type, id)));
    for (let i = 0; i < list.length; i += 150) {
      const chunk = list.slice(i, i + 150);
      try {
        const got = await api.approvalStates(w, type, chunk);
        for (const id of chunk) states.set(key(w, type, id), got[id] ?? null);
      } catch {
        // Chips are an enrichment: a failed lookup leaves the list as it is (and is not retried in a loop).
        for (const id of chunk) states.set(key(w, type, id), null);
      }
    }
  }
  for (const fn of loadedListeners) fn();
}

function useLoadedTick(): number {
  const [n, setN] = useState(0);
  useEffect(() => {
    const fn = () => setN((x) => x + 1);
    loadedListeners.add(fn);
    return () => {
      loadedListeners.delete(fn);
    };
  }, []);
  return n;
}

/** Forget one item's cached state (after its work was saved / replaced): its chip reloads, nothing else does. */
export function refreshApprovalState(workspaceId: string, type: ApprovalTargetType, id: string): void {
  states.delete(key(workspaceId, type, id));
  for (const fn of loadedListeners) fn();
}

/** The approval state of the listed items (for their chips); a hidden or deleted item has no entry. */
export function useApprovalStates(type: ApprovalTargetType, ids: readonly string[], workspaceId?: string | null): Record<string, ApprovalItemStateView> {
  const session = useSession();
  const w = workspaceId ?? session.workspace?.id ?? null;
  const tick = useApprovalsVersion();
  const loaded = useLoadedTick();
  const joined = useMemo(() => [...new Set(ids)].sort().join(','), [ids]);

  useEffect(() => {
    if (!w || !joined) return;
    for (const id of joined.split(',')) if (!states.has(key(w, type, id))) want(w, type, id);
  }, [w, type, joined, tick, loaded]);

  const out: Record<string, ApprovalItemStateView> = {};
  if (w) for (const id of ids) {
    const s = states.get(key(w, type, id));
    if (s) out[id] = s;
  }
  return out;
}

/** One item's state (a list row, the editor top bar, the product page, the file pane). `undefined` while loading / unknown. */
export function useApprovalState(type: ApprovalTargetType, id: string | null | undefined, workspaceId?: string | null): ApprovalItemStateView | undefined {
  const ids = useMemo(() => (id ? [id] : []), [id]);
  return useApprovalStates(type, ids, workspaceId)[id ?? ''];
}

// ---------- summary (nav badge, home) ----------

const summaries = new Map<string, { at: number; value: ApprovalSummary }>();
const inflight = new Map<string, Promise<ApprovalSummary>>();
const TTL_MS = 60_000;

export function useApprovalSummary(): ApprovalSummary | null {
  const session = useSession();
  const w = session.workspace?.id ?? null;
  const tick = useApprovalsVersion();
  const [summary, setSummary] = useState<ApprovalSummary | null>(() => (w ? summaries.get(w)?.value ?? null : null));

  useEffect(() => {
    if (!w || session.status !== 'authenticated') return;
    const hit = summaries.get(w);
    if (hit && Date.now() - hit.at < TTL_MS) {
      setSummary(hit.value);
      return;
    }
    let alive = true;
    let p = inflight.get(w);
    if (!p) {
      p = api.approvalSummary(w).finally(() => inflight.delete(w));
      inflight.set(w, p);
    }
    p.then((value) => {
      summaries.set(w, { at: Date.now(), value });
      if (alive) setSummary(value);
    }).catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [w, tick, session.status]);

  return summary;
}
